import { useEffect, useMemo, useState } from "react";

const FARBEN = [
  "var(--accent)",
  "var(--food-golden)",
  "var(--food-basil)",
  "var(--food-sea)",
  "var(--food-berry)",
  "var(--food-lime)",
];

/**
 * Streuwert zwischen 0 und 1, allein aus einer Zahl gerechnet.
 *
 * Math.random ist im Render verboten, weil React eine Komponente jederzeit
 * noch einmal zeichnen darf und die Schnipsel dann springen würden. Diese
 * Funktion liefert zu derselben Zahl immer denselben Wert, sieht aber
 * ungeordnet genug aus, dass niemand ein Muster erkennt.
 */
function streuung(n: number): number {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * Ein kurzer Wurf Konfetti.
 *
 * Genau einmal in der App, beim letzten Haken einer Liste. Ein Fest, das jedes
 * Mal kommt, ist keines mehr, deshalb hängt es nicht an jedem Abhaken, sondern
 * am Leerwerden. Die Schnipsel liegen über allem, nehmen aber keine Antippung
 * an, und nach knapp zwei Sekunden räumt die Komponente sich selbst ab.
 */
export function Konfetti({ seed = 0, onDone }: { seed?: number; onDone?: () => void }) {
  const [sichtbar, setSichtbar] = useState(true);

  const schnipsel = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => {
        const a = streuung(seed * 7 + i);
        const b = streuung(seed * 7 + i + 100);
        const c = streuung(seed * 7 + i + 200);
        return {
          id: i,
          links: 8 + a * 84,
          weit: Math.round((b - 0.5) * 220),
          hoch: 220 + Math.round(c * 300),
          dreh: Math.round(180 + a * 540),
          dauer: 1100 + Math.round(b * 500),
          takt: Math.round(c * 260),
          farbe: FARBEN[i % FARBEN.length],
          breit: 6 + Math.round(a * 5),
          rund: b > 0.55,
        };
      }),
    [seed],
  );

  useEffect(() => {
    const t = window.setTimeout(() => {
      setSichtbar(false);
      onDone?.();
    }, 1900);
    return () => {
      window.clearTimeout(t);
    };
  }, [onDone]);

  if (!sichtbar) return null;

  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-50 overflow-hidden" data-testid="konfetti">
      {schnipsel.map((s) => (
        <span
          key={s.id}
          className="absolute top-[14vh] block"
          style={{
            left: `${String(s.links)}%`,
            width: s.breit,
            height: s.rund ? s.breit : s.breit * 1.8,
            background: s.farbe,
            borderRadius: s.rund ? "999px" : "2px",
            animation: `konfetti ${String(s.dauer)}ms var(--ease-out) ${String(s.takt)}ms both`,
            ["--weit" as string]: `${String(s.weit)}px`,
            ["--hoch" as string]: `${String(s.hoch)}px`,
            ["--dreh" as string]: `${String(s.dreh)}deg`,
          }}
        />
      ))}
    </div>
  );
}
